import type { Middleware } from '@reduxjs/toolkit';
import { isFulfilled, isRejected } from '@reduxjs/toolkit';

import { setErrorMessage, setSuccessMessage } from 'app/settings/slices';
import type { RTKQueryMeta } from 'types';
import type { ErrorMessage } from 'app/settings/types';
import { isJSON } from 'utils/utils';
import { getError } from 'utils/helpers';

type ErrorMeta = {
  skipErrorHandler?: boolean;
  includeErrorStatusCode?: boolean;
};

// for actions with meta
function hasMeta(action: unknown): action is { meta: unknown } {
  return typeof action === 'object' && action !== null && 'meta' in action;
}

export const messageMiddleware: Middleware = ({ dispatch }) => (next) => (action) => {
  const result = next(action);

  let meta: RTKQueryMeta | undefined;
  if (hasMeta(action)) {
    meta = action.meta as RTKQueryMeta;
  }

  if (!meta?.arg) return result;

  if (isFulfilled(action)) {
    const successMessage = meta.baseQueryMeta?.successMessage;

    if (successMessage) {
      dispatch(setSuccessMessage(successMessage));
    }

    return result;
  }

  if (isRejected(action)) {
    const rawMessage = action.error?.message;

    let skipErrorHandler = false;
    let includeErrorStatusCode = false;
    type ErrorResponseShape = { response?: { status?: number; data?: unknown } };
    let errorInput: unknown = action.error as unknown;
    // const errorByName = action.error as { name?: string; message?: string } | undefined;
    //
    // // todo check this case deeper
    // if (
    //   errorByName?.name === 'ConditionError'
    // ) {
    //   return result;
    // }

    if (typeof rawMessage === 'string' && isJSON(rawMessage)) {
      const parsed = JSON.parse(rawMessage) as {
        meta?: ErrorMeta;
        response?: unknown;
      };

      if (parsed.meta?.skipErrorHandler) {
        skipErrorHandler = true;
      }

      includeErrorStatusCode = parsed.meta?.includeErrorStatusCode ?? false;

      errorInput = parsed ?? action.error;
    }

    if (skipErrorHandler) {
      return result;
    }

    const parsedError = getError(
      errorInput as ErrorResponseShape,
      includeErrorStatusCode,
    ) as ErrorMessage;

    dispatch(setErrorMessage(parsedError));
  }

  return result;
};
